import React from "react";
import { motion } from "framer-motion";
import { Song } from "../../types/song";
import { useTheme } from "../../context/ThemeContext";
import { getHeroCtaUrl } from "../../config/platforms";
import { ARTIST_NAME } from "../../config/site";
import { PlayFilledIcon } from "./PlatformIcons";

function formatReleaseDate(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

interface SongHeroProps {
  song: Song;
}

const SongHero: React.FC<SongHeroProps> = ({ song }) => {
  const { currentTheme } = useTheme();
  const ctaUrl = getHeroCtaUrl(song);
  const releaseDate = formatReleaseDate(song.releaseDate);

  const handleListenNow = () => {
    if (ctaUrl) return; // anchor handles navigation
    const fallback = document.getElementById("song-player") || document.getElementById("song-video") || document.getElementById("song-lyrics");
    fallback?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className={`relative overflow-hidden bg-gradient-to-br ${currentTheme.colors.primary} text-white pt-10 pb-12 px-6`}>
      {/* Ambient background glows — subtle, matches the site's existing float-slow motif */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-float-slow" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-float-slower" />

      <div className="relative max-w-4xl mx-auto flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          whileHover={{ scale: 1.03 }}
          className="relative mb-5 group"
        >
          <div className="absolute -inset-3 bg-gradient-to-r from-purple-500/40 to-pink-500/40 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          {song.coverImage ? (
            <img
              src={song.coverImage}
              alt={`${song.title} album artwork`}
              width={288}
              height={288}
              fetchPriority="high"
              className="relative w-52 h-52 sm:w-64 sm:h-64 rounded-2xl shadow-2xl object-cover border border-white/10"
            />
          ) : (
            <div className="relative w-52 h-52 sm:w-64 sm:h-64 rounded-2xl shadow-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
              <PlayFilledIcon className="w-16 h-16 text-white/60" />
            </div>
          )}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-4xl sm:text-5xl md:text-6xl font-bold mb-2 text-shadow-lg"
        >
          {song.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-xl md:text-2xl text-purple-200 mb-1"
        >
          {ARTIST_NAME}
        </motion.p>

        {releaseDate && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-sm md:text-base text-purple-300/80 mb-5 tracking-wide uppercase"
          >
            Released {releaseDate}
          </motion.p>
        )}

        <motion.a
          href={ctaUrl || undefined}
          target={ctaUrl ? "_blank" : undefined}
          rel={ctaUrl ? "noopener noreferrer" : undefined}
          onClick={handleListenNow}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3.5 px-9 rounded-full text-lg shadow-2xl min-h-[56px] cursor-pointer"
        >
          <PlayFilledIcon className="w-5 h-5" />
          Listen Now
        </motion.a>
      </div>
    </section>
  );
};

export default SongHero;
